import React from 'react';
import i18n from 'util/i18n';
import { Summary, DateSummary } from 'components/Summary';
import { CitizenshipForeignPassportsItemModel } from 'forms/sections/citizenship/foreignPassports/types';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate, CommonOnError, CommonScrollIntoView, CommonRequired
} from 'types/props';
import Field from 'components/Form/Field';
import Country from 'components/Form/Country';
import DateControl from 'components/Form/DateControl';
import Name from 'components/Form/Name';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Accordion from 'components/Form/Accordion';
import Location from 'components/Form/Location';
import Text from 'components/Form/Text';
import Comments from 'components/Form/Comments';
import TravelItem from './TravelItem';

interface Props extends ValidationElementProps, CitizenshipForeignPassportsItemModel {
  onUpdate?: CommonOnUpdate;
  onError?: CommonOnError;
  className?: string;
  scrollIntoView?: CommonScrollIntoView;
  required?: CommonRequired;
  attribute?: string;
  errors?: any;
  defaultState?: boolean;
}

export default class PassportItem extends ValidationElement<Props> {
  constructor(props) {
    super(props);
    this.updateCountryFocused = this.updateCountryFocused.bind(this);
  }

  handleNameError = (value, arr) => {
    const arr2 = arr.map((err) => ({
      ...err,
      code:
        err.code.indexOf('required') !== -1
          ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
          : err.code,
    }));
    return this.props.onError(value, arr2);
  };

  update = (queue: Partial<CitizenshipForeignPassportsItemModel>) => {
    this.props.onUpdate({
      Country: this.props.Country,
      Issued: this.props.Issued,
      Location: this.props.Location,
      Name: this.props.Name,
      Number: this.props.Number,
      Expiration: this.props.Expiration,
      Used: this.props.Used,
      Countries: this.props.Countries,
      Comments: this.props.Comments,
      CountriesComments: this.props.CountriesComments,
      UsedComments: this.props.UsedComments,
      ...queue,
    });
  };

  updateCountry = (values: CitizenshipForeignPassportsItemModel['Country']) => {
    this.update({
      Country: values,
    });
  };

  updateIssued = (values: CitizenshipForeignPassportsItemModel['Issued']) => {
    this.update({
      Issued: values,
    });
  };

  updateLocation = (values: CitizenshipForeignPassportsItemModel['Location']) => {
    this.update({
      Location: values,
    });
  };

  updateName = (values: CitizenshipForeignPassportsItemModel['Name']) => {
    this.update({
      Name: values,
    });
  };

  updateNumber = (values: CitizenshipForeignPassportsItemModel['Number']) => {
    this.update({
      Number: values,
    });
  };

  updateExpiration = (values: CitizenshipForeignPassportsItemModel['Expiration']) => {
    this.update({
      Expiration: values,
    });
  };

  updateUsed = (values: CitizenshipForeignPassportsItemModel['Used']) => {
    this.update({
      Used: values,
    });
  };

  updateCountries = (values: CitizenshipForeignPassportsItemModel['Countries']) => {
    this.update({
      Countries: values,
    });
  };

  updateComments = (values: CitizenshipForeignPassportsItemModel['Comments']) => {
    this.update({
      Comments: values,
    });
  };

  updateUsedComments = (values: CitizenshipForeignPassportsItemModel['UsedComments']) => {
    this.update({
      UsedComments: values,
    });
  };

  updateCountriesComments = (values: CitizenshipForeignPassportsItemModel['CountriesComments']) => {
    this.update({
      CountriesComments: values,
    });
  };

  updateCountryFocused(focus: boolean) {
    this.update({
      countryFocused: focus,
    });
  }

  summary = (item, index) => {
    const itemProperties = (item || {}).Item || {};
    const dates = DateSummary(itemProperties.Dates);
    const country = itemProperties.Country && itemProperties.Country.value ? itemProperties.Country.value : '';

    return Summary({
      type: i18n.t('citizenship.multiple.collection.travel.summary.item'),
      index,
      left: country,
      right: dates,
      placeholder: i18n.t('citizenship.multiple.collection.travel.summary.unknown'),
    });
  };

  render() {
    const { errors } = this.props;
    const accordionErrors = errors && errors.filter((e) => e.indexOf('Countries.accordion') === 0);

    const attribute = `Passports.items[${this.props.$index}].Item`;

    return (
      <div className="passport-item">
        <Field
          title={i18n.t('citizenship.multiple.heading.passport.country')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Country`}
        >
          <Country
            attribute={`${attribute}.Country`}
            name="Country"
            className="passport-country"
            {...this.props.Country}
            updateCountryFocused={this.updateCountryFocused}
            onUpdate={this.updateCountry}
            onError={this.props.onError}
            required={this.props.required}
            excludeUnitedStates
          />
        </Field>

        <Field
          title={i18n.t('citizenship.multiple.heading.passport.issued')}
          adjustFor="labels"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Issued`}
        >
          <DateControl
            attribute={`${attribute}.Issued`}
            name="Issued"
            {...this.props.Issued}
            className="passport-issued"
            minDateEqualTo
            onUpdate={this.updateIssued}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('citizenship.multiple.heading.passport.location')}
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Location`}
        >
          <Location
            attribute={`${attribute}.Location`}
            name="Location"
            {...this.props.Location}
            layout={Location.CITY_COUNTRY}
            className="passport-location"
            onUpdate={this.updateLocation}
            onError={this.props.onError}
            required={this.props.required}
            excludeUnitedStates={false}
          />
        </Field>

        <Field
          title={i18n.t('citizenship.multiple.heading.passport.name')}
          optional
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Name`}
        >
          <Name
            aria-label="Provide the name in which your passport was issued"
            attribute={`${attribute}.Name`}
            name="Name"
            {...this.props.Name}
            className="passport-name"
            onUpdate={this.updateName}
            onError={this.handleNameError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>

        <Field
          title={i18n.t('citizenship.multiple.heading.passport.number')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Number`}
        >
          <Text
            ariaLabel="Provide the passport (or identity card) number for this passport"
            attribute={`${attribute}.Number`}
            name="Number"
            {...this.props.Number}
            className="passport-number"
            onUpdate={this.updateNumber}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('citizenship.multiple.heading.passport.expiration')}
          adjustFor="labels"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Expiration`}
        >
          <DateControl
            attribute={`${attribute}.Expiration`}
            name="Expiration"
            {...this.props.Expiration}
            prefix="passport.expiration"
            className="passport-expiration"
            onUpdate={this.updateExpiration}
            onError={this.props.onError}
            minDate={this.props.Issued}
            minDateEqualTo
            noMaxDate
            required={this.props.required}
          />
        </Field>
        <Comments
          title={i18n.t('citizenship.comments.optional')}
          name={`${attribute}Comments`}
          className="optional-comment"
          attribute={`${attribute}Comments`}
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        />

        <Branch
          attribute={`${attribute}.Used`}
          name="Used"
          label={i18n.t('citizenship.multiple.heading.passport.used')}
          labelSize="h4"
          className="passport-used"
          {...this.props.Used}
          onUpdate={this.updateUsed}
          onError={this.props.onError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
        />
        <Comments
          title={i18n.t('citizenship.comments.optional')}
          name={`${attribute}UsedComments`}
          className="optional-comment"
          attribute={`${attribute}UsedComments`}
          {...this.props.UsedComments}
          onUpdate={this.updateUsedComments}
          required={false}
        />
        <Show when={this.props.Used.value === 'Yes'}>
          <Accordion
            {...this.props.Countries}
            className="foreign-countries"
            defaultState={this.props.defaultState}
            onUpdate={this.updateCountries}
            onError={this.props.onError}
            summary={this.summary}
            description={i18n.t('citizenship.multiple.collection.travel.summary.title')}
            required={this.props.required}
            errors={accordionErrors}
            scrollIntoView={this.props.scrollIntoView}
            appendLabel={i18n.t('citizenship.multiple.collection.travel.append')}
          >
            <TravelItem
              attribute={`${attribute}.Countries`}
              name="Item"
              bind
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </Accordion>
          <Comments
            title={i18n.t('citizenship.comments.optional')}
            name={`${attribute}CountriesComments`}
            className="optional-comment"
            attribute={`${attribute}CountriesComments`}
            {...this.props.CountriesComments}
            onUpdate={this.updateCountriesComments}
            required={false}
          />
        </Show>
      </div>
    );
  }
}

PassportItem.defaultProps = {
  Country: {},
  Issued: {},
  Location: {},
  Name: {},
  Number: {},
  Expiration: {},
  Used: {},
  Countries: {},
  countryFocused: false,
  Comments: {},
  CountriesComments: {},
  UsedComments: {},
  onUpdate: () => { },
  onError: (value, arr) => arr,
  defaultState: true,
};
